import {getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import $ from 'jquery';
import React from 'react';

import {ElementMouseEvent, RectBounds} from 'util/Types';

// Somewhat arbitrary cutoffs to ensure columns aren't extremely resized.
const AUTO_RESIZE_MIN_COLUMN_WIDTH = 35;
const AUTO_RESIZE_MAX_COLUMN_WIDTH = 500;
// Keep in sync with MIN_COLUMN_WIDTH in benchling/notebook/plain_tables/validation.py
const MIN_SIZE = 20;

export interface CellResizerProps {
  // Used when comparing this.props to prevProps in componentDidUpdate
  widths: Array<number>;
  setColumnWidth: (cellId: string, width: number) => void;
  allowAutoResize: boolean;
  tableRef: React.RefObject<HTMLTableElement>;
  stickyHeaderRef: React.RefObject<HTMLDivElement>;
  /* The sticky header ref may exist even when it is not visible. So we only use it when it is visible. */
  isStickyHeaderVisible: boolean;
}

interface State {
  active: boolean;
  index: number | null;
  minBound: number | null;
  position: number | null;
  /**
   * When hovering over the sticky header, we must raise the z-index of the cell resizer to be above the
   * sticky header. (We can't always be in this state or else the filldown handle is underneath the resizer)
   */
  raiseResizeLine: boolean;
  stickyHeaderRef: HTMLDivElement | null;
}

function getInitialState(): State {
  return {
    active: false,
    index: null, // the column index of the column to the left of the resize line
    minBound: null, // the pixel position of the left's column non-dragged side
    position: null, // the current pixel position of the resize line
    raiseResizeLine: false,
    stickyHeaderRef: null,
  };
}

// This component should be a sibling of a <table> that has <col data-id={id}>s which can be resized.

// Note: this component could be refactored to also support vertical resizing,
//   cs: td -> tr, left -> top, pageX -> pageY,
//  css: width -> height, *left -> *top, ew-resize -> ns-resize
//  remove requirement for last boundary cell / maxPosition

export default class CellResizer extends React.Component<CellResizerProps, State> {
  rootRef = React.createRef<HTMLDivElement>();

  override state: State = getInitialState();

  override componentDidMount(): void {
    this.setState({stickyHeaderRef: this.props.stickyHeaderRef.current});
    // Listen to movement on the table wrapper for positioning/constraining the resize line
    // (we look at the wrapper since it contains both the table and sticky header)
    this.addEventListeners();
  }

  override componentDidUpdate(prevProps: CellResizerProps): void {
    if (
      prevProps.tableRef !== this.props.tableRef ||
      this.state.stickyHeaderRef !== this.props.stickyHeaderRef?.current ||
      prevProps.isStickyHeaderVisible !== this.props.isStickyHeaderVisible
    ) {
      this.setState({stickyHeaderRef: this.props.stickyHeaderRef.current});
      this.removeEventListeners(prevProps);
      this.addEventListeners();
    }
  }

  override componentWillUnmount(): void {
    this.removeEventListeners(this.props);
  }

  addEventListeners(): void {
    this.props.tableRef.current?.addEventListener('mousemove', this.positionInactiveResizeLine);
    this.props.tableRef.current?.addEventListener('mousemove', this.raiseInactiveResizeLine);
    this.props.stickyHeaderRef.current?.addEventListener('mousemove', this.positionInactiveResizeLine);
    this.props.stickyHeaderRef.current?.addEventListener('mousemove', this.raiseInactiveResizeLine);
    $(window).on('mouseup', this.finalizeAndDeactivateResize);
  }

  removeEventListeners(props: CellResizerProps): void {
    props.tableRef.current?.removeEventListener('mousemove', this.positionInactiveResizeLine);
    props.tableRef.current?.removeEventListener('mousemove', this.raiseInactiveResizeLine);
    props.stickyHeaderRef.current?.removeEventListener('mousemove', this.positionInactiveResizeLine);
    props.stickyHeaderRef.current?.removeEventListener('mousemove', this.raiseInactiveResizeLine);
    $(window).off('mouseup', this.finalizeAndDeactivateResize);
  }

  /**
   * Normalizes an x-coordinate that is relative to the left of the page, to be relative to the
   * left of the parent.
   * @param  {Number} pos  the x-coordinate to be normalized
   * @return {Number}      a normalized coordinate relative to the table's left.
   */
  normalizePos(pos: number): number {
    return pos - $(this.rootRef.current!).parent().offset()!.left;
  }

  getBounds($td: JQuery): {min: number; max: number} {
    const min = this.normalizePos($td.offset()!.left);
    const max = min + $td.outerWidth();
    return {min, max};
  }

  getBoundingBoxOfChildren = ($parent: JQuery): RectBounds => {
    const children = $parent.children();
    if (!children.length) {
      return {top: 0, left: 0, bottom: 0, right: 0};
    }

    let top = Infinity;
    let left = Infinity;
    let bottom = -Infinity;
    let right = -Infinity;

    for (const element of Array.from(children)) {
      const rect = element.getBoundingClientRect();
      top = Math.min(top, rect.top + window.scrollY);
      left = Math.min(left, rect.left + window.scrollX);
      bottom = Math.max(bottom, rect.bottom + window.scrollY);
      right = Math.max(right, rect.right + window.scrollX);
    }

    return {top, left, bottom, right};
  };

  handleResetState = (): void => {
    this.setState(getInitialState());
  };

  /**
   * Sets the position of the resizer between the columns corresponding to `index` and `index + 1`
   */
  setPositionForIndex(index: number): void {
    if (this.props.tableRef.current == null) {
      this.handleResetState();
      return;
    }

    const query = $(this.props.tableRef.current);

    // If we are at boundary, disable dragging.
    if (index < 0 || !query.find(`col:eq(${index})`).is('[data-id]')) {
      this.handleResetState();
      return;
    }

    // Query for the td corresponding to `index` to compute its horizontal bounds.
    // Note: we arbitrarily choose the first row since all tds in the same column have the same width.
    const $td = query.find(`tr:first td:eq(${index})`).first();
    if (!$td.length) {
      // The <table> may have <col>s without <tr>s, so there's no <td> to position against
      this.handleResetState();
      return;
    }

    // Otherwise, determine the bounds that we can resize to.
    const bounds = this.getBounds($td);

    this.setState({
      minBound: bounds.min,
      position: bounds.max,
      index,
    });
  }

  positionInactiveResizeLine = (e: MouseEvent): void => {
    if (this.state.active) {
      return;
    }

    // Find the two tds that we are stradling
    const position = this.normalizePos(e.pageX);
    const $td = $(e.target!).closest('td');
    if (!$td.length) {
      return;
    }
    const {min, max} = this.getBounds($td);

    // Compute the index of the left column
    const index = position < (min + max) / 2 ? $td.index() - 1 : $td.index();

    this.setPositionForIndex(index);
  };

  /**
   * Gets the table column header row. Virtualized tables have an actual (CSS-managed) sticky first row.
   * Non-virtualized tables have a separately-rendered duplicate header row that floats above the table.
   * See: https://github.com/benchling/aurelia/pull/77628 for additional context on the sticky header.
   */
  getTableColumnHeaderRow(): JQuery {
    if (this.props.stickyHeaderRef?.current != null && this.props.isStickyHeaderVisible) {
      return $(this.props.stickyHeaderRef.current).find('tr:first');
    }

    return $(this.props.tableRef.current!).find('tr:first');
  }

  // We currently use a single resize handle for both the column header and the column cells. These two regions
  // have distinct z-index behaviors so that the cells render beneath the column header when the table has a vertical
  // scroll region.
  //
  // This creates a conflict between z-indices, as the filldown handle (from cell selection) should both be above
  // the resize handle and below the column header.
  //
  // To resolve this, we raise the z-index of the resize handle when the mouse is over the column header.
  raiseInactiveResizeLine = (e: MouseEvent): void => {
    const $headerRow = this.getTableColumnHeaderRow();
    const childBounds = this.getBoundingBoxOfChildren($headerRow);
    const raiseResizeLine = this.isInBoundingBox(childBounds, {x: e.pageX, y: e.pageY});
    this.setState({raiseResizeLine});
  };

  /** Returns true when the x and y coordinates are inside the client rectangle of the element */
  isInBoundingBox({top, left, bottom, right}: RectBounds, {x, y}: {x: number; y: number}): boolean {
    const width = right - left;
    const height = bottom - top;
    return x > left && x < left + width && y > top && y < top + height;
  }

  activateResize = (e: ElementMouseEvent): void => {
    e.preventDefault(); // don't steal focus from anyone.
    this.setState({active: true});
    $(window).one('mouseup', this.finalizeAndDeactivateResize);
  };

  positionActiveResizeLine = (e: ElementMouseEvent): void => {
    if (!this.state.active) {
      return;
    }
    const position = this.normalizePos(e.pageX);
    const minPosition = this.state.minBound! + MIN_SIZE;
    this.setState({position: Math.max(minPosition, position)});
  };

  finalizeAndDeactivateResize = (): void => {
    if (!this.state.active) {
      return;
    }

    const {minBound, position, index} = this.state;
    const cellId = $(this.props.tableRef.current!).find(`col:eq(${index})`).attr('data-id');
    const width = Math.floor(position! - minBound!);
    this.props.setColumnWidth(cellId, width);
    // Only reset active, so that resizer is still positioned for double-click or a subsequent drag resize
    this.setState({active: false});
  };

  applyAutoResize = (): void => {
    // Columns widths are persisted, so for auto-resizing a column, we must measure the widest content and
    // then configure the column to be that wide. To do so, we temporarily add the `cell-measure` class to all
    // <td>s in the column, which is styled with:
    //    a. `position: absolute` to ensure the <td> is not constrained in width by its parent <table>,
    //    b. `width: max-content` to have the <td> grow to fit its contents.
    // From here, we simply iterate over the <td>s to find the max-measured width, and then reset the CSS.
    // NOTE: Because this happens synchronously in JS, the user will not visibly see a screen repaint.
    if (this.state.index == null) {
      return;
    }
    // Note: nth-child is 1-indexed
    const $tds = $(this.props.tableRef.current!).find(`td:nth-child(${this.state.index + 1})`);
    $tds.addClass('cell--measure');
    const width = Math.ceil(
      Math.min(
        AUTO_RESIZE_MAX_COLUMN_WIDTH,
        Math.max(
          AUTO_RESIZE_MIN_COLUMN_WIDTH,
          ...Array.from($tds).map((td) => td.getBoundingClientRect().width)
        )
      )
    );
    $tds.removeClass('cell--measure');
    const cellId = $(this.props.tableRef.current!).find(`col:eq(${this.state.index})`).attr('data-id');
    this.props.setColumnWidth(cellId, width);
    this.handleResetState();
  };

  override render(): JSX.Element {
    const left = this.state.position || undefined;
    return (
      <div
        aria-disabled={this.state.index == null}
        className={classNames('cellResizer', {
          'is-disabled': this.state.index == null,
          'is-raised': this.state.raiseResizeLine || this.state.active,
        })}
        data-state={this.state.raiseResizeLine || this.state.active ? 'raised' : 'default'}
        ref={this.rootRef}
        {...getTestProps({component: 'CellResizer', element: 'root'})}
        onMouseDown={this.activateResize}
        onMouseMove={this.positionActiveResizeLine}
        onDoubleClick={this.props.allowAutoResize ? this.applyAutoResize : undefined}
      >
        <div
          // Hidden <div> that ensures the clickable region of our component is not too small.
          className={classNames('cellResizer-hitArea', {
            'is-raised': this.state.raiseResizeLine || this.state.active,
          })}
          {...getTestProps({component: 'CellResizer', element: 'hitArea'})}
          style={{left}}
        />
        {this.state.active && (
          // While the user is actively resizing, show:
          //  - a blue line indicating where the column is being resized to
          //  - a backdrop to prevent accidental interaction with the underlying table.
          <>
            <div
              className="cellResizer-line"
              {...getTestProps({component: 'CellResizer', element: 'line'})}
              style={{left}}
            />
            <div className="backdrop" {...getTestProps({component: 'CellResizer', element: 'backdrop'})} />
          </>
        )}
      </div>
    );
  }
}
